import { Vec3 } from './vec3';

export class Quat {
  constructor(public x = 0, public y = 0, public z = 0, public w = 1) {}

  set(x: number, y: number, z: number, w: number): this { this.x = x; this.y = y; this.z = z; this.w = w; return this; }
  copy(q: Quat): this { this.x = q.x; this.y = q.y; this.z = q.z; this.w = q.w; return this; }
  clone(): Quat { return new Quat(this.x, this.y, this.z, this.w); }
  identity(): this { return this.set(0, 0, 0, 1); }

  setAxisAngle(axis: Vec3, angle: number): this {
    const h = angle * 0.5, s = Math.sin(h);
    this.x = axis.x * s; this.y = axis.y * s; this.z = axis.z * s; this.w = Math.cos(h);
    return this;
  }
  /** Yaw about Y, then pitch about X, then roll about Z (intrinsic YXZ). */
  setEuler(yaw: number, pitch: number, roll = 0): this {
    const cy = Math.cos(yaw * 0.5), sy = Math.sin(yaw * 0.5);
    const cp = Math.cos(pitch * 0.5), sp = Math.sin(pitch * 0.5);
    const cr = Math.cos(roll * 0.5), sr = Math.sin(roll * 0.5);
    this.x = cy * sp * cr + sy * cp * sr;
    this.y = sy * cp * cr - cy * sp * sr;
    this.z = cy * cp * sr - sy * sp * cr;
    this.w = cy * cp * cr + sy * sp * sr;
    return this;
  }
  setYaw(yaw: number): this { return this.setAxisAngle(Vec3.UP, yaw); }

  /** this = this * q */
  multiply(q: Quat): this {
    const ax = this.x, ay = this.y, az = this.z, aw = this.w;
    const bx = q.x, by = q.y, bz = q.z, bw = q.w;
    this.x = ax * bw + aw * bx + ay * bz - az * by;
    this.y = ay * bw + aw * by + az * bx - ax * bz;
    this.z = az * bw + aw * bz + ax * by - ay * bx;
    this.w = aw * bw - ax * bx - ay * by - az * bz;
    return this;
  }
  /** this = q * this */
  premultiply(q: Quat): this {
    const r = q.clone().multiply(this);
    return this.copy(r);
  }
  conjugate(): this { this.x = -this.x; this.y = -this.y; this.z = -this.z; return this; }
  invert(): this { return this.conjugate().normalize(); }
  lengthSq(): number { return this.x * this.x + this.y * this.y + this.z * this.z + this.w * this.w; }
  normalize(): this {
    let l = Math.sqrt(this.lengthSq());
    if (l < 1e-10) return this.identity();
    l = 1 / l; this.x *= l; this.y *= l; this.z *= l; this.w *= l;
    return this;
  }
  dot(q: Quat): number { return this.x * q.x + this.y * q.y + this.z * q.z + this.w * q.w; }

  slerp(q: Quat, t: number): this {
    if (t <= 0) return this;
    if (t >= 1) return this.copy(q);
    let cos = this.dot(q);
    let bx = q.x, by = q.y, bz = q.z, bw = q.w;
    if (cos < 0) { cos = -cos; bx = -bx; by = -by; bz = -bz; bw = -bw; }
    let s0: number, s1: number;
    if (cos > 0.9995) { s0 = 1 - t; s1 = t; }
    else {
      const omega = Math.acos(cos), so = Math.sin(omega);
      s0 = Math.sin((1 - t) * omega) / so;
      s1 = Math.sin(t * omega) / so;
    }
    this.x = s0 * this.x + s1 * bx; this.y = s0 * this.y + s1 * by;
    this.z = s0 * this.z + s1 * bz; this.w = s0 * this.w + s1 * bw;
    return this.normalize();
  }

  /** Rotate a vector in place by this quaternion. */
  rotate(v: Vec3): Vec3 {
    const qx = this.x, qy = this.y, qz = this.z, qw = this.w;
    const vx = v.x, vy = v.y, vz = v.z;
    const tx = 2 * (qy * vz - qz * vy);
    const ty = 2 * (qz * vx - qx * vz);
    const tz = 2 * (qx * vy - qy * vx);
    v.x = vx + qw * tx + (qy * tz - qz * ty);
    v.y = vy + qw * ty + (qz * tx - qx * tz);
    v.z = vz + qw * tz + (qx * ty - qy * tx);
    return v;
  }

  /** Shortest rotation taking unit vector a to unit vector b. */
  setFromUnitVectors(a: Vec3, b: Vec3): this {
    let r = a.dot(b) + 1;
    if (r < 1e-6) {
      r = 0;
      if (Math.abs(a.x) > Math.abs(a.z)) { this.x = -a.y; this.y = a.x; this.z = 0; this.w = r; }
      else { this.x = 0; this.y = -a.z; this.z = a.y; this.w = r; }
    } else {
      this.x = a.y * b.z - a.z * b.y;
      this.y = a.z * b.x - a.x * b.z;
      this.z = a.x * b.y - a.y * b.x;
      this.w = r;
    }
    return this.normalize();
  }

  /** Extract yaw (rotation about Y) in radians, where yaw 0 faces +Z. */
  getYaw(): number {
    const f = this.rotate(new Vec3(0, 0, 1));
    return Math.atan2(f.x, f.z);
  }

  angleTo(q: Quat): number {
    return 2 * Math.acos(Math.min(1, Math.abs(this.dot(q))));
  }

  /** Rotate toward q by at most maxAngle radians. */
  rotateTowards(q: Quat, maxAngle: number): this {
    const a = this.angleTo(q);
    if (a < 1e-6) return this.copy(q);
    return this.slerp(q, Math.min(1, maxAngle / a));
  }

  toArray(out: number[] | Float32Array = [], offset = 0): typeof out {
    out[offset] = this.x; out[offset + 1] = this.y; out[offset + 2] = this.z; out[offset + 3] = this.w; return out;
  }
}
